import * as React from 'react';
import classNames from 'classnames';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {Currency, CurrencyDisplayType, Text, Tag, Icon} from 'components/UIKit';
import * as locales from '../../../locales';
import styles from '../ListingMetrics.scss';
import {ListingMetric as ListingMetricType} from '../../../types';

type LiElementProps = JSX.IntrinsicElements['li'];

export interface ListingMetricProps extends LiElementProps {
  metric: ListingMetricType;
  suffix?: string;
  condensed?: boolean;
  hideWarning?: boolean;
  showVerified?: boolean;
  currencyDisplay?: CurrencyDisplayType;
  wrapperComponent?: string | React.ComponentClass;
}

function ListingMetricComponent({
  i18n,
  metric,
  suffix = '',
  condensed = false,
  hideWarning = false,
  showVerified = false,
  currencyDisplay = 'subhead',
  wrapperComponent = 'li',
  ...restProps
}: ListingMetricProps & WithI18nProps) {
  const {title, value, warning, currency = false, percentage = false} = metric;

  const Wrapper = wrapperComponent;
  const displayValue = percentage
    ? value
    : parseInt(value, 10).toLocaleString('en-US', {maximumFractionDigits: 0});

  const showWarning = !hideWarning && warning;

  return (
    <Wrapper className={styles.Metric} {...restProps}>
      <Text resetMargin text={title}>
        {showWarning && (
          <span className={styles.Warning}>
            <Icon size={16} icon="alertTriangle" fill="#647482" />
          </span>
        )}
      </Text>
      {currency ? (
        <Currency value={Number(value)} display={currencyDisplay} condensed={condensed} />
      ) : (
        <span
          className={classNames(styles.Text, {
            [styles.Condensed]: condensed,
          })}
        >
          {displayValue}
          {percentage && <Text secondary text="%" />}
          {suffix && <Text secondary className={styles.Suffix} text={suffix} />}
        </span>
      )}
      {showVerified && metric.verified ? (
        <Tag
          tag={i18n.translate('verified')}
          icon="shopify"
          iconProps={{size: 16}}
          className={styles.Verified}
        />
      ) : null}
    </Wrapper>
  );
}

export const ListingMetric = withI18n({
  id: 'Listing',
  fallback: locales.en,
})(ListingMetricComponent);

export default ListingMetric;
